// ── QuickRevenueEntry — 빠른 매출 입력 모달 ────────────────────
// 2가지 입력 모드:
//   A) 총액 입력: 카드/현금/배달 분할 (기본)
//   B) 메뉴별 입력: 카테고리 menu items 수량 × 단가 → 자동 합계
// 거래장부에 income transaction(들)으로 저장
// 메뉴별 모드는 lineItems[]에 메뉴 판매 데이터 첨부
import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { normalizeMenuName, isMenuMatch } from '../utils/menuProfitability';
import { L, M, MU, trCountry } from '../i18n/L';   // V160 중국어
import BiName from '../i18n/BiName';   // V165 메뉴명 한중 병기
import { translateMany } from '../utils/translate';
import { classOfMenu, menuClassMap, mixOfLineItems, splitMix, SALES_CLASS_LABEL, isSalesClass } from '../utils/salesMix';   // V166 음식 · 주류·음료

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

// ── V167: 다시 열면 그날 입력한 매출을 그대로 불러온다 ─────────────────────────────
// 사장님: "매출 입력 후 다시 열고 추가 수정하려고 하는데 기존 입력은 다 지워지고 새로 추가하는 것만 잡혀"
//   총액(결제수단별) · 그중 주류·음료 · 메뉴별 수량 · 메뉴 없이 입력한 금액 · 결제수단 비율을 그날 기록에서 채운다
const methodOf = (t) => {
  const sub = String((t && t.category && t.category.sub) || (t && t.revenueCategory) || '').toLowerCase();
  if (sub === 'sales_card' || sub === 'card') return 'sales_card';
  if (sub === 'sales_delivery' || sub === 'delivery') return 'sales_delivery';
  return 'sales_cash';
};
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export function preloadDay(dayIncomes = [], menus = [], classByNorm) {
  const amounts = { sales_card: 0, sales_cash: 0, sales_delivery: 0 };
  const menuQtys = {};
  let total = 0; let liTotal = 0; let liBev = 0; let bevKnown = 0; let hasLines = false;
  for (const t of dayIncomes || []) {
    const amt = (Number(t.supply) || 0) + (Number(t.vat) || 0);
    total += amt;
    amounts[methodOf(t)] = r2(amounts[methodOf(t)] + amt);
    if (t.mix && (Number(t.mix.food) || 0) + (Number(t.mix.bev) || 0) > 0) bevKnown += Number(t.mix.bev) || 0;
    if (!Array.isArray(t.lineItems)) continue;
    for (const li of t.lineItems) {
      const rawName = li && (li.name || li.menuId);
      const qty = Number(li && (li.qty ?? li.quantity)) || 0;
      if (!rawName || qty <= 0) continue;
      hasLines = true;
      const lt = Number(li.total ?? li.amount) || qty * (Number(li.unitPrice) || 0);
      liTotal += lt;
      const m = mixOfLineItems([li], classByNorm);
      liBev += m.bev;
      const matched = (menus || []).find(x => isMenuMatch(x.name, rawName));
      if (matched) menuQtys[matched.id] = (menuQtys[matched.id] || 0) + qty;
    }
  }
  total = r2(total);
  const extra = Math.max(0, r2(total - liTotal));
  const bev = r2(bevKnown || liBev);
  let split = null;
  if (total > 0) {
    const ids = ['sales_card', 'sales_cash', 'sales_delivery'];
    split = {}; let used = 0;
    const last = [...ids].reverse().find(id => amounts[id] > 0);
    for (const id of ids) {
      if (id === last) { split[id] = Math.max(0, 100 - used); continue; }
      split[id] = Math.round(amounts[id] / total * 100); used += split[id];
    }
  }
  // V168: V166에서 카드 비율 0%로 메뉴별 입력을 저장하면 메뉴 수량(lineItems)이 빠졌다 — 메모 '(메뉴별 입력 n종)'만 남음
  let lostMenus = 0;
  if (!hasLines) {
    for (const t of dayIncomes || []) {
      const m = String((t && t.memo) || '').match(/메뉴별 입력 (\d+)종/);
      if (m) lostMenus = Math.max(lostMenus, Number(m[1]) || 0);
    }
  }
  return {
    total, amounts, bev, menuQtys, split, lostMenus,
    extra,                                   // 메뉴 줄로 설명되지 않는 금액 (총액으로만 넣은 날은 전부)
    extraBev: Math.max(0, r2(bev - liBev)),
    mode: hasLines ? 'menus' : 'total',
  };
}

const REVENUE_TYPES = [
  { id: 'sales_card',     get label() { return L("카드"); },  icon: '💳', color: '#1A56A0', get placeholder() { return L("카드 매출 합계"); } },
  { id: 'sales_cash',     get label() { return L("현금"); },  icon: '💵', color: '#18181B', get placeholder() { return L("현금 매출 합계"); } },
  { id: 'sales_delivery', get label() { return L("배달"); },  icon: '🛵', color: '#EA580C', get placeholder() { return L("배달앱 매출 합계"); } },
];

export default function QuickRevenueEntry({
  isOpen,
  defaultDate,        // 'YYYY-MM-DD' or null
  categories = [],    // [{ name, items: [{ id, name, type, selling_price, ... }] }]
  transactions = [],  // 과거 거래장부 — legacy 메뉴 추출용
  onClose,
  onSave,             // async (transactions[]) => void
  isMobile,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState('total');  // 'total' | 'menus'
  const [date, setDate] = useState(defaultDate || today);
  const [amounts, setAmounts] = useState({ sales_card: 0, sales_cash: 0, sales_delivery: 0 });
  const [menuQtys, setMenuQtys] = useState({});  // { menuId: qty }
  const [paymentSplit, setPaymentSplit] = useState({ sales_card: 70, sales_cash: 20, sales_delivery: 10 });
  const [memo, setMemo] = useState('');
  const [bevAmount, setBevAmount] = useState(0);   // V166 총액 입력: 그중 주류·음료 매출
  const [extra, setExtra] = useState(0);           // V167 메뉴별 입력: 메뉴 없이 입력한 매출 (예전에 총액으로 넣은 것)
  const [extraBev, setExtraBev] = useState(0);
  const [preTotal, setPreTotal] = useState(0);     // 불러온 그날 매출 합계
  const [lostMenus, setLostMenus] = useState(0);   // V168 메뉴 수량이 저장 안 된 기록 (n종)
  const touched = useRef(false);                   // 손댔으면 뒤에서 바뀐 기록으로 덮지 않는다
  const loadedFor = useRef('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 사용 가능한 메뉴 — 현재 레시피 메뉴 + 과거 legacy 메뉴
  // V165: 메뉴명 한중 병기 — 레시피의 중국어 이름(name_zh). 판매 중단 메뉴는 같은 이름 레시피·거래 기록의 중국어로
  const zhByNorm = useMemo(() => {
    const m = new Map();
    (categories || []).forEach(cat => (cat.items || []).forEach(it => {
      const zh = String(it?.name_zh || '').trim();
      if (it?.name && zh) m.set(normalizeMenuName(it.name), zh);
    }));
    return m;
  }, [categories]);
  const classByNorm = useMemo(() => menuClassMap(categories), [categories]);   // V166
  const availableMenus = useMemo(() => {
    const out = [];
    const seenNorm = new Set();

    // 1) 현재 레시피 메뉴
    (categories || []).forEach(cat => {
      (cat.items || []).forEach(it => {
        if (it.type !== 'menu') return;
        const price = Number(it.selling_price) || 0;
        if (price <= 0) return;
        const norm = normalizeMenuName(it.name);
        if (seenNorm.has(norm)) return;
        seenNorm.add(norm);
        out.push({
          id: it.id || it.name,
          name: it.name,
          name_zh: String(it.name_zh || '').trim(),
          cls: classOfMenu(it, cat.name),   // V166 음식 · 음료 · 주류
          price,
          category: cat.name || '',
          isLegacy: false,
        });
      });
    });

    // 2) 거래장부 lineItems에서 발견된 legacy 메뉴
    const legacyAgg = {};
    (transactions || []).forEach(t => {
      if (t?.type !== 'income') return;
      if (!Array.isArray(t.lineItems)) return;
      for (const li of t.lineItems) {
        const rawName = li.name || li.menuId;
        if (!rawName) continue;
        const norm = normalizeMenuName(rawName);
        if (!norm) continue;
        const matchedToCurrent = out.some(m => isMenuMatch(m.name, rawName));
        if (matchedToCurrent) continue;
        if (seenNorm.has(norm)) continue;
        const q = Number(li.qty) || Number(li.quantity) || 0;
        const rev = Number(li.total) || Number(li.amount) || (q * (Number(li.unitPrice) || 0));
        if (!legacyAgg[norm]) { legacyAgg[norm] = { name: rawName, name_zh: String(li.name_zh || '').trim() || zhByNorm.get(norm) || '', cls: isSalesClass(li.cls) ? li.cls : 'food', qty: 0, revenue: 0 }; }
        legacyAgg[norm].qty += q;
        legacyAgg[norm].revenue += rev;
      }
    });

    const legacyList = Object.entries(legacyAgg)
      .map(([norm, info]) => ({
        id: `legacy_${norm}`,
        name: info.name,
        name_zh: info.name_zh,
        cls: info.cls,
        price: info.qty > 0 ? Math.round(info.revenue / info.qty) : 0,
        category: '판매 중단',
        isLegacy: true,
        legacyQty: info.qty,
      }))
      .filter(m => m.price > 0)
      .sort((a, b) => b.legacyQty - a.legacyQty);

    legacyList.forEach(m => {
      seenNorm.add(normalizeMenuName(m.name));
      out.push(m);
    });

    return out;
  }, [categories, transactions, zhByNorm]);

  // 모달 열 때 reset — 날짜는 열 때 받은 날짜로
  useEffect(() => {
    if (!isOpen) return;
    setDate(defaultDate || today);
    setMemo('');
    setError('');
    touched.current = false;
    loadedFor.current = '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultDate]);

  // V166: 고른 날짜의 기존 매출 — 저장하면 새로 입력한 내용으로 바뀐다 (덮어쓰기)
  //   예전엔 이 값을 만들지 않아 저장을 누르면 오류로 멈추고(화면엔 아무 표시 없음) 저장이 안 됐다
  const existingDayIncomes = useMemo(
    () => (transactions || []).filter(t => t && t.type === 'income' && t.date === date),
    [transactions, date]);

  // V167: 그날 기록 불러오기 — 열 때 · 날짜를 바꿀 때 · (아직 손대기 전) 기록이 늦게 도착했을 때
  const daySig = existingDayIncomes.map(t => `${t.id}:${(Number(t.supply) || 0) + (Number(t.vat) || 0)}`).join('|');
  useEffect(() => {
    if (!isOpen) return;
    const key = `${date}#${daySig}#${availableMenus.length}`;
    if (loadedFor.current === key) return;
    const sameDate = loadedFor.current.split('#')[0] === date;
    if (sameDate && touched.current) return;
    loadedFor.current = key;
    touched.current = false;
    const pre = preloadDay(existingDayIncomes, availableMenus, classByNorm);
    setAmounts(pre.amounts);
    setBevAmount(pre.bev);
    setMenuQtys(pre.menuQtys);
    setExtra(pre.extra);
    setExtraBev(pre.extraBev);
    setPreTotal(pre.total);
    setLostMenus(pre.lostMenus || 0);
    setPaymentSplit(pre.split || { sales_card: 70, sales_cash: 20, sales_delivery: 10 });
    setMode(pre.mode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, date, daySig, availableMenus]);

  // ⚠️ 모든 hook은 early return 위에 있어야 함 (React hook order 규칙)
  const [trZh, setTrZh] = useState({});
  useEffect(() => {
    if (!isOpen) return undefined;
    const need = availableMenus.filter(m => !m.name_zh && !trZh[m.name] && /[가-힣]/.test(m.name)).map(m => m.name);
    if (!need.length) return undefined;
    let alive = true;
    translateMany(need, 'menu').then(map => {
      if (!alive || !map || !map.size) return;
      setTrZh(prev => { const next = { ...prev }; for (const n of need) { const z = map.get(n); if (z) next[n] = z; } return next; });
    }).catch(() => {});
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, availableMenus]);

  const menuLineItems = useMemo(() => {
    return availableMenus
      .filter(m => (Number(menuQtys[m.id]) || 0) > 0)
      .map(m => {
        const zh = m.name_zh || trZh[m.name] || '';
        return {
          menuId: m.id, name: m.name,
          ...(zh ? { name_zh: zh } : {}),   // V165 거래 기록에도 중국어 (판매 중단 뒤에도 병기)
          cls: m.cls || 'food',              // V166 음식 · 음료 · 주류
          qty: Number(menuQtys[m.id]) || 0,
          unitPrice: m.price,
          total: (Number(menuQtys[m.id]) || 0) * m.price,
        };
      });
  }, [availableMenus, menuQtys, trZh]);

  if (!isOpen) return null;

  // 합계 계산
  const totalFromAmounts = Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0);
  const totalFromMenus = r2(menuLineItems.reduce((s, l) => s + l.total, 0) + (Number(extra) || 0));   // V167 메뉴 없이 입력한 매출 포함
  const total = mode === 'total' ? totalFromAmounts : totalFromMenus;
  // V166: 그날 매출의 음식 · 주류·음료
  const menuMix = mixOfLineItems(menuLineItems);
  const dayMix = mode === 'total'
    ? { food: Math.max(0, total - (Number(bevAmount) || 0)), bev: Math.min(total, Number(bevAmount) || 0) }
    : { food: menuMix.food + Math.max(0, (Number(extra) || 0) - Math.min(Number(extra) || 0, extraBev)), bev: menuMix.bev + Math.min(Number(extra) || 0, extraBev) };

  const handleSave = async () => {
    if (total <= 0) {
      setError(L("매출 금액을 입력해주세요"));
      return;
    }
    if (mode === 'total' && (Number(bevAmount) || 0) > total) {
      setError(L("주류·음료 매출이 합계보다 커요"));
      return;
    }
    // 기존 거래 덮어쓰기 안전 확인 — 5건 이상 또는 합계 차이 30% 이상이면 confirm
    if (existingDayIncomes.length > 0) {
      const existingTotal = existingDayIncomes.reduce((s, t) => s + ((Number(t.supply) || 0) + (Number(t.vat) || 0)), 0);
      const diff = Math.abs(total - existingTotal);
      const diffPct = existingTotal > 0 ? (diff / existingTotal * 100) : 0;
      if (existingDayIncomes.length >= 5 || diffPct >= 30) {
        const ok = window.confirm(
          L("⚠️ {0} 기존 매출 {1}건 (합계 {2})을 삭제하고\n", date, existingDayIncomes.length, M(existingTotal)) +
          L("새 매출 {0}으로 덮어씁니다.\n\n", M(total)) +
          L("(차이: {0}%)\n\n", Math.round(diffPct)) +
          L("계속할까요?")
        );
        if (!ok) return;
      }
    }
    setSaving(true);
    setError('');
    const unitR0 = trCountry() === 'CN' ? (n) => Math.round(n * 100) / 100 : (n) => Math.round(n);   // V166
    try {
      const txs = [];
      if (mode === 'total') {
        // 모드 A: 카테고리별로 transaction 생성
        for (const t of REVENUE_TYPES) {
          const amt = Number(amounts[t.id]) || 0;
          if (amt <= 0) continue;
          const supply = unitR0(amt / 1.1);
          const vat = unitR0(amt - supply);
          txs.push({
            type: 'income',
            date,
            supply, vat, total: amt,
            partnerId: null, partnerName: '',
            evidence: { type: t.id === 'sales_cash' ? 'cash' : 'card', isQualified: true, sourceUrl: null, externalId: null },
            category: { major: null, sub: null },
            revenueCategory: t.id,
            itemName: '',
            mix: splitMix(amt, dayMix.food, dayMix.bev),   // V166 음식 · 주류·음료
            ...(txs.length === 0 && menuLineItems.length ? { lineItems: menuLineItems } : {}),   // V167 그날 메뉴 기록은 그대로
            memo: memo || `${t.label} 매출 (빠른 입력)`,
            vatTreatment: { deductible: false, presumed: false, presumedRate: null },
          });
        }
      } else {
        // 모드 B: 결제수단 분할 기준으로 transaction 1개씩 생성 + lineItems에 메뉴 데이터 첨부
        const splitTotal = (paymentSplit.sales_card + paymentSplit.sales_cash + paymentSplit.sales_delivery) || 100;
        // V166: 나눈 금액의 합이 합계와 딱 맞게 (마지막 결제수단이 나머지) · 중국 매장은 0.01위안 단위
        const unitR = trCountry() === 'CN' ? (n) => Math.round(n * 100) / 100 : (n) => Math.round(n);
        const parts = REVENUE_TYPES.filter(t => (paymentSplit[t.id] || 0) > 0);
        let left = total;
        for (const t of REVENUE_TYPES) {
          const ratio = (paymentSplit[t.id] || 0) / splitTotal;
          if (ratio <= 0) continue;
          const isLast = parts[parts.length - 1] === t;
          const amt = isLast ? unitR(left) : unitR(total * ratio);
          left = unitR(left - amt);
          if (amt <= 0) continue;
          const supply = unitR(amt / 1.1);
          const vat = unitR(amt - supply);
          txs.push({
            type: 'income',
            date,
            supply, vat, total: amt,
            partnerId: null, partnerName: '',
            evidence: { type: t.id === 'sales_cash' ? 'cash' : 'card', isQualified: true, sourceUrl: null, externalId: null },
            category: { major: null, sub: null },
            revenueCategory: t.id,
            itemName: '',
            mix: splitMix(amt, dayMix.food, dayMix.bev),   // V166 음식 · 주류·음료
            // 메뉴 판매 데이터 — 첫 transaction에만 lineItems 첨부 (중복 방지)
            ...(txs.length === 0 ? { lineItems: menuLineItems } : {}),   // V167 첫 거래에 (카드 비율 0이어도)
            memo: memo || `${t.label} 매출 (메뉴별 입력 ${menuLineItems.length}종)`,
            vatTreatment: { deductible: false, presumed: false, presumedRate: null },
          });
        }
      }
      // 같은 날짜 기존 매출 거래 ID들 → 부모가 삭제하도록 전달 (덮어쓰기 패턴)
      const replaceIds = existingDayIncomes.map(t => t.id).filter(Boolean);
      await onSave(txs, { replaceIds, replaceDate: date });
      onClose();
    } catch (e) {
      setError(e.message || L("저장 실패"));
    } finally {
      setSaving(false);
    }
  };

  const edit = (setter) => (v) => { touched.current = true; setter(v); };   // V167
  // ── 모달 ──
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff',
        borderRadius: 16,
        padding: isMobile ? '20px 18px' : '24px 26px',
        maxWidth: 520, width: '100%',
        maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 12px 60px rgba(0,0,0,0.3)',
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12,
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a1a' }}>
              {L("✏️ 빠른 매출 입력")}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              {L("거래장부에 자동 저장 · 메뉴별 입력 시 이익률 정확도 ↑")}
            </div>
          </div>
          <button onClick={onClose} aria-label={L("닫기")} style={{
            width: 30, height: 30, borderRadius: '50%',
            border: '1px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 16, cursor: 'pointer',
          }}>×</button>
        </div>

        {/* 모드 토글 */}
        <div style={{
          display: 'flex', gap: 4,
          background: '#f3f4f6', borderRadius: 9, padding: 4,
          marginBottom: 14,
        }}>
          <ModeBtn active={mode === 'total'} onClick={() => setMode('total')}>
            {L("💰 총액 입력")}
          </ModeBtn>
          <ModeBtn active={mode === 'menus'} onClick={() => setMode('menus')}
            disabled={availableMenus.length === 0}
            tip={availableMenus.length === 0 ? L("판매가 등록된 메뉴 없음") : null}>
            {L("🍽️ 메뉴별 입력")} {availableMenus.length > 0 && L("({0}종)", availableMenus.length)}
          </ModeBtn>
        </div>

        {/* 날짜 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 5 }}>
            {L("📅 매출 날짜")}
          </label>
          <input type="date" value={date} max={today}
            onChange={e => setDate(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 8,
              fontSize: 14, outline: 'none',
            }}
          />
        </div>

        {/* ── 모드 A: 총액 입력 ── */}
        {mode === 'total' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 6 }}>
              {L("💰 결제 수단별 매출 (입력 안 한 항목은 0원)")}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {REVENUE_TYPES.map(t => (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px',
                  background: amounts[t.id] > 0 ? `${t.color}10` : '#fafbfc',
                  borderRadius: 8,
                  border: `1.5px solid ${amounts[t.id] > 0 ? t.color : '#e5e7eb'}`,
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{t.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.color, width: 40, flexShrink: 0 }}>
                    {t.label}
                  </span>
                  <input type="number" min="0"
                    value={amounts[t.id] || ''}
                    placeholder={t.placeholder}
                    onChange={e => edit(setAmounts)({ ...amounts, [t.id]: Number(e.target.value) || 0 })}
                    style={{
                      flex: 1, minWidth: 0,
                      padding: '7px 10px',
                      border: '1px solid #e5e7eb', borderRadius: 6,
                      fontSize: 14, textAlign: 'right',
                      outline: 'none', background: '#fff',
                    }}
                  />
                  <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>{MU()}</span>
                </div>
              ))}
              {/* V166 그중 주류·음료 */}
              <div data-qr-bev style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '8px 12px', background: Number(bevAmount) > 0 ? '#EFF6FF' : '#fafbfc',
                borderRadius: 8, border: `1.5px dashed ${Number(bevAmount) > 0 ? '#1D4ED8' : '#e5e7eb'}`,
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1D4ED8', flexShrink: 0 }}>🍺🥤 {L("그중 주류·음료")}</span>
                <input type="number" min="0" data-qr-bev-input
                  value={bevAmount || ''}
                  placeholder={L("없으면 비워 두세요")}
                  onChange={e => edit(setBevAmount)(Math.max(0, Number(e.target.value) || 0))}
                  style={{ flex: '1 1 100px', minWidth: 0, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, textAlign: 'right', outline: 'none', background: '#fff' }} />
                <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>{MU()}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── 모드 B: 메뉴별 입력 ── */}
        {mode === 'menus' && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 6 }}>
                {L("🍽️ 메뉴별 판매 수량")}
              </label>
              <div style={{
                maxHeight: 280, overflowY: 'auto',
                border: '1px solid #e5e7eb', borderRadius: 8,
                background: '#fafbfc',
              }}>
                {availableMenus.map((m, idx) => {
                  const qty = Number(menuQtys[m.id]) || 0;
                  const prevLegacy = idx > 0 && availableMenus[idx - 1].isLegacy;
                  const showDivider = m.isLegacy && !prevLegacy;
                  return (
                    <Fragment key={m.id}>
                      {showDivider && (
                        <div style={{
                          padding: '6px 10px', fontSize: 10, color: '#52525B',
                          background: '#FAFAFA', borderBottom: '1px solid #f0f0f0',
                          fontWeight: 700,
                        }}>
                          {L("⚠️ 판매 중단 메뉴 (과거 거래장부에서 발견)")}
                        </div>
                      )}
                    <div data-qr-row style={{
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      padding: '8px 10px',
                      borderBottom: '1px solid #f0f0f0',
                      background: qty > 0 ? '#FAFAFA' : (m.isLegacy ? '#fafaf7' : 'transparent'),
                      opacity: m.isLegacy && qty === 0 ? 0.7 : 1,
                    }}>
                      <span data-qr-menu={m.name} style={{
                        flex: '1 1 130px', minWidth: 0,
                        fontSize: 13, fontWeight: qty > 0 ? 700 : 500,
                        color: m.isLegacy ? '#6b7280' : '#1a1a1a',
                        overflowWrap: 'anywhere', lineHeight: 1.3,
                      }}>
                        {m.isLegacy && <span style={{ marginRight: 4, fontSize: 10 }}>💤</span>}
                        {m.cls && m.cls !== 'food' && <span data-qr-cls={m.cls} title={L(SALES_CLASS_LABEL[m.cls].ko)} style={{ marginRight: 4, fontSize: 12 }}>{SALES_CLASS_LABEL[m.cls].icon}</span>}
                        <BiName ko={m.name} zh={m.name_zh || trZh[m.name] || ''} pron={false} />
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
                      <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0, width: 56, textAlign: 'right' }}>
                        {M(m.price)}
                      </span>
                      <button onClick={() => edit(setMenuQtys)({ ...menuQtys, [m.id]: Math.max(0, qty - 1) })}
                        style={{ width: 24, height: 24, border: '1px solid #e5e7eb',
                                 background: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: 14 }}>−</button>
                      <input type="number" min="0"
                        value={qty || ''}
                        placeholder="0"
                        onChange={e => edit(setMenuQtys)({ ...menuQtys, [m.id]: Math.max(0, Number(e.target.value) || 0) })}
                        style={{
                          width: 50, padding: '4px 6px',
                          border: '1px solid #e5e7eb', borderRadius: 5,
                          fontSize: 13, textAlign: 'center',
                          outline: 'none', background: '#fff',
                        }}
                      />
                      <button onClick={() => edit(setMenuQtys)({ ...menuQtys, [m.id]: qty + 1 })}
                        style={{ width: 24, height: 24, border: '1px solid #FF6F0F',
                                 background: '#FAFAFA', color: '#18181B',
                                 borderRadius: 5, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>+</button>
                      <span style={{
                        fontSize: 11, color: qty > 0 ? '#FF6F0F' : '#9ca3af',
                        fontWeight: 700, width: 60, textAlign: 'right', flexShrink: 0,
                      }}>{M(qty * m.price)}</span>
                      </span>
                    </div>
                    </Fragment>
                  );
                })}
              </div>
              {/* V167 메뉴 없이 입력한 매출 (예전에 총액으로 넣은 것) — 지우지 않고 그대로 더한다 */}
              {(extra > 0 || preTotal > 0) && (
                <div data-qr-extra style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6, padding: '8px 10px', borderRadius: 8, background: '#FFFBEB', border: '1px dashed #F59E0B' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#92400E', flex: '1 1 160px' }}>📝 {L("메뉴 없이 입력한 매출")}</span>
                  <input type="number" min="0" data-qr-extra-input value={extra || ''} placeholder="0"
                    onChange={e => edit(setExtra)(Math.max(0, Number(e.target.value) || 0))}
                    style={{ width: 110, padding: '5px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, textAlign: 'right', background: '#fff' }} />
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{MU()}</span>
                  {extra > 0 && <button type="button" data-qr-extra-clear onClick={() => edit(setExtra)(0)}
                    style={{ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, fontSize: 11, padding: '4px 8px', color: '#6b7280', cursor: 'pointer' }}>{L("비우기")}</button>}
                  {lostMenus > 0 && (
                    <div data-qr-lost style={{ flexBasis: '100%', fontSize: 11, color: '#B45309', lineHeight: 1.5 }}>
                      {L("이 날은 메뉴별로 입력했는데({0}종) 메뉴 수량이 저장되지 않았어요. 위에 메뉴 수량을 다시 넣고 이 칸은 비워 주세요.", lostMenus)}
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                {L("💡 메뉴별 입력 시 메뉴 이익률 랭킹이 실제 판매 기반으로 정확해져요")}
              </div>
            </div>

            {/* 결제수단 분할 % */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 6 }}>
                {L("💳 결제수단 비율 (%) — 합계 자동으로 분배")}
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                {REVENUE_TYPES.map(t => (
                  <div key={t.id} data-qr-split={t.id} style={{
                    flex: 1, minWidth: 0, padding: '6px 8px',
                    background: `${t.color}10`,
                    border: `1px solid ${t.color}40`,
                    borderRadius: 7,
                    display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 11, color: t.color, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}><span style={{ fontSize: 12, marginRight: 3 }}>{t.icon}</span>{t.label}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, flex: '1 1 52px', minWidth: 52 }}>
                    <input type="number" min="0" max="100"
                      value={paymentSplit[t.id]}
                      onChange={e => edit(setPaymentSplit)({ ...paymentSplit, [t.id]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                      style={{
                        flex: 1, minWidth: 0, width: '100%', padding: '3px 5px',
                        border: 'none', background: '#fff',
                        borderRadius: 4, fontSize: 12,
                        textAlign: 'right', outline: 'none',
                      }}
                    />
                    <span style={{ fontSize: 10, color: '#6b7280' }}>%</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* 합계 */}
        <div style={{
          padding: '10px 14px',
          background: total > 0 ? '#FAFAFA' : '#f3f4f6',
          borderRadius: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: total > 0 ? '#FF6F0F' : '#9ca3af' }}>
            {L("이날 매출 합계")}
            {mode === 'menus' && menuLineItems.length > 0 &&
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>
                {' '}{L("· {0}종", menuLineItems.length)} {menuLineItems.reduce((s, l) => s + l.qty, 0)}{L("개 판매")}
              </span>}
          </span>
          <span style={{ fontSize: 18, fontWeight: 900, color: total > 0 ? '#FF6F0F' : '#9ca3af' }}>
            {M(total)}
          </span>
        </div>
        {/* V166 음식 · 주류·음료 */}
        {total > 0 && dayMix.bev > 0 && (
          <div data-qr-mix style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#3f3f46', margin: '-6px 4px 12px' }}>
            <span>🍽️ {L("음식")} <b>{M(dayMix.food)}</b></span>
            <span style={{ color: '#1D4ED8' }}>🍺🥤 {L("주류·음료")} <b>{M(dayMix.bev)}</b></span>
          </div>
        )}
        {/* V166 그날 이미 있는 매출 — 저장하면 바뀐다 */}
        {existingDayIncomes.length > 0 && (
          <div data-qr-existing style={{ fontSize: 11, color: '#9A3412', background: '#FFF7ED', borderRadius: 8, padding: '7px 10px', marginBottom: 12 }}>
            {L("이 날 입력한 매출 {0}을 불러왔어요 — 고치거나 더한 뒤 저장하면 이 내용으로 바뀌어요", M(preTotal))}
          </div>
        )}

        {/* 메모 */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, display: 'block', marginBottom: 5 }}>
            {L("📝 메모 (선택)")}
          </label>
          <input type="text"
            value={memo} onChange={e => setMemo(e.target.value)}
            placeholder={L("예: 비 와서 매출 적음 / 단체 회식 30명")}
            style={{
              width: '100%', padding: '9px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 8,
              fontSize: 13, outline: 'none',
            }}
          />
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', background: '#F4F4F5',
            color: '#52525B', borderRadius: 8,
            fontSize: 12, marginBottom: 10,
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* 안내 */}
        <div style={{
          padding: '8px 12px', background: '#FAFAFA',
          borderRadius: 8, fontSize: 11, color: '#78350f',
          lineHeight: 1.5, marginBottom: 12,
        }}>
          {L("💡 부가세 포함 금액으로 저장 (공급가액 = 입력값 ÷ 1.1, 부가세 = 10%). 거래장부에서 수정 가능.")}
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{
            flex: 1, padding: '11px',
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 13, fontWeight: 700,
            borderRadius: 9, cursor: 'pointer',
          }}>{L("취소")}</button>
          <button onClick={handleSave} disabled={saving || total <= 0} style={{
            flex: 2, padding: '11px',
            border: 'none',
            background: total > 0 && !saving ? '#EA580C' : '#9ca3af',
            color: '#fff', fontSize: 13, fontWeight: 800,
            borderRadius: 9, cursor: total > 0 && !saving ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {saving ? L("⏳ 저장 중...") : L("✅ {0} 저장", M(total))}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 모드 토글 버튼 ──
function ModeBtn({ active, onClick, disabled, tip, children }) {
  return (
    <button onClick={onClick} disabled={disabled} title={tip || ''} style={{
      flex: 1, padding: '8px 10px',
      border: 'none',
      background: active ? '#FF6F0F' : 'transparent',
      color: active ? '#fff' : (disabled ? '#9ca3af' : '#6b7280'),
      fontSize: 12, fontWeight: 800,
      borderRadius: 6,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s',
      opacity: disabled ? 0.6 : 1,
    }}>{children}</button>
  );
}
